import React, { Children } from 'react';
import { useParams } from 'react-router-dom';
import { HeartIcon, UsersIcon, BookIcon, StoreIcon, BuildingIcon, ClockIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import BackButton from '../../components/UI/BackButton';
// Mock data - in a real app this would come from an API or shared data file
const donationDetails = {
  authors: {
    label: 'Authors',
    icon: UsersIcon,
    color: 'blue',
    totalDonors: 245,
    totalAmount: 18500,
    description: 'Authors contributing a portion of their book sales and royalties to support literacy and education.',
    topGenres: [{
      genre: 'Romance',
      percentage: 35
    }, {
      genre: 'Fantasy',
      percentage: 25
    }, {
      genre: 'Mystery',
      percentage: 20
    }],
    nonprofits: [{
      name: "Children's Literacy Foundation",
      theme: 'Early Childhood Education',
      amount: 5500,
      donationCount: 89,
      mainContributors: 'Romance Authors'
    }, {
      name: 'Book Access for All',
      theme: 'Educational Equity',
      amount: 7000,
      donationCount: 102,
      mainContributors: 'Fantasy Authors'
    }, {
      name: 'Libraries First',
      theme: 'Public Libraries',
      amount: 6000,
      donationCount: 54,
      mainContributors: 'Mystery Authors'
    }]
  },
  readers: {
    label: 'Readers',
    icon: BookIcon,
    color: 'emerald',
    totalDonors: 312,
    totalAmount: 5500,
    description: 'Readers rounding up purchases and making direct donations to support their favorite causes.',
    topGenres: [{
      genre: 'Dark Romance',
      percentage: 30
    }, {
      genre: 'Urban Fantasy',
      percentage: 28
    }, {
      genre: 'Thriller',
      percentage: 22
    }],
    nonprofits: [{
      name: 'Rural Library Project',
      theme: 'Rural Education',
      amount: 2000,
      donationCount: 156,
      mainContributors: 'Dark Romance Readers'
    }, {
      name: 'Literacy for All',
      theme: 'Adult Literacy',
      amount: 2000,
      donationCount: 98,
      mainContributors: 'Urban Fantasy Readers'
    }, {
      name: 'Books for Schools',
      theme: 'K-12 Education',
      amount: 1500,
      donationCount: 58,
      mainContributors: 'Thriller Readers'
    }]
  },
  vendors: {
    label: 'Vendors',
    icon: StoreIcon,
    color: 'amber',
    totalDonors: 156,
    totalAmount: 8500,
    description: 'Service providers and merch creators giving back to the community that supports them.',
    topGenres: [{
      genre: 'Romance Merchandise',
      percentage: 40
    }, {
      genre: 'Fantasy Art',
      percentage: 35
    }, {
      genre: 'Literary Gifts',
      percentage: 25
    }],
    nonprofits: [{
      name: 'Community Book Banks',
      theme: 'Book Distribution',
      amount: 3000,
      donationCount: 45,
      mainContributors: 'Romance Merchandise Vendors'
    }, {
      name: 'Reading Is Fundamental',
      theme: 'Youth Literacy',
      amount: 3500,
      donationCount: 67,
      mainContributors: 'Fantasy Art Vendors'
    }, {
      name: 'Library Innovation Fund',
      theme: 'Library Technology',
      amount: 2000,
      donationCount: 44,
      mainContributors: 'Literary Gifts Vendors'
    }]
  },
  platform: {
    label: 'Platform',
    icon: BuildingIcon,
    color: 'rose',
    totalDonors: 1,
    totalAmount: 15000,
    description: 'LitLaunch matching contributions and dedicated platform initiatives.',
    topInitiatives: [{
      name: 'International Education',
      percentage: 40
    }, {
      name: 'Digital Access',
      percentage: 35
    }, {
      name: 'Industry Support',
      percentage: 25
    }],
    nonprofits: [{
      name: 'Global Literacy Foundation',
      theme: 'International Education',
      amount: 5000,
      donationCount: 1,
      mainContributors: 'Platform Initiative'
    }, {
      name: 'Digital Library Initiative',
      theme: 'Digital Access',
      amount: 5000,
      donationCount: 1,
      mainContributors: 'Platform Initiative'
    }, {
      name: 'Book Industry Charitable Foundation',
      theme: 'Industry Support',
      amount: 5000,
      donationCount: 1,
      mainContributors: 'Platform Initiative'
    }]
  }
};
const recentDonations = [{
  amount: 1250,
  cause: "Children's Literacy Foundation",
  donor: 'LitLaunch Community',
  date: '2023-12-01',
  genre: 'Romance',
  type: 'platform'
}, {
  amount: 750,
  cause: 'Book Access for All',
  donor: 'Author Alliance',
  date: '2023-11-30',
  genre: 'Fantasy',
  type: 'authors'
}, {
  amount: 500,
  cause: 'Libraries First',
  donor: "Reader's Circle",
  date: '2023-11-29',
  genre: 'Dark Romance',
  type: 'readers'
}, {
  amount: 980,
  cause: 'Rural Library Project',
  donor: 'Fantasy Authors Guild',
  date: '2023-11-28',
  genre: 'Urban Fantasy',
  type: 'authors'
}, {
  amount: 1500,
  cause: 'Literacy for All',
  donor: 'Thriller Writers Association',
  date: '2023-11-27',
  genre: 'Thriller',
  type: 'vendors'
}, {
  amount: 650,
  cause: 'Books for Schools',
  donor: 'Mystery Book Club',
  date: '2023-11-26',
  genre: 'Mystery',
  type: 'readers'
}];
const CategoryDetail = () => {
  const {
    type
  } = useParams<{
    type: string;
  }>();
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const categoryKey = type as keyof typeof donationDetails;
  const data = donationDetails[categoryKey];
  if (!data) {
    return <div className={`min-h-screen ${dk.page} flex items-center justify-center`} data-id="element-9388" data-mp="3fw9abybeylz4">
        Category not found
      </div>;
  }
  const Icon = data.icon;
  const colorMap: Record<string, {
    bg: string;
    text: string;
    ring: string;
  }> = {
    blue: {
      bg: 'bg-blue-50',
      text: 'text-blue-600',
      ring: 'ring-blue-500'
    },
    emerald: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-600',
      ring: 'ring-emerald-500'
    },
    amber: {
      bg: 'bg-amber-50',
      text: 'text-amber-600',
      ring: 'ring-amber-500'
    },
    rose: {
      bg: 'bg-rose-50',
      text: 'text-rose-600',
      ring: 'ring-rose-500'
    }
  };
  const colors = colorMap[data.color];
  // Filter donations for this category
  const categoryDonations = recentDonations.filter(d => {
    if (type === 'authors' && (d.type === 'authors' || d.donor.includes('Author'))) return true;
    if (type === 'readers' && (d.type === 'readers' || d.donor.includes('Reader'))) return true;
    if (type === 'vendors' && (d.type === 'vendors' || d.donor.includes('Vendor'))) return true;
    if (type === 'platform' && (d.type === 'platform' || d.donor.includes('LitLaunch'))) return true;
    return false;
  });
  return <div className={`min-h-screen ${dk.page}`} data-id="element-9389" data-mp="1acheyhy26hui">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-9390" data-mp="15c34yucwcxtu">
        <BackButton to="/donations" className="mb-6" data-id="element-9391" data-mp="15a8j9dtsx1op" />

        {/* Hero Header */}
        <div className={`${dk.card} rounded-xl p-8 mb-8 border shadow-sm`} data-id="element-9392" data-mp="10c72x0nrfggx">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6" data-id="element-9393" data-mp="3q2kcoaftc394">
            <div className={`p-4 rounded-full ${darkMode ? 'bg-gray-700' : colors.bg}`} data-id="element-9394" data-mp="3l2o9gdexwfum">
              <Icon className={`h-10 w-10 ${colors.text}`} data-id="element-9395" data-mp="1oz1d7cr3qd12" />
            </div>
            <div className="flex-1" data-id="element-9396" data-mp="1tzp47yfbytva">
              <h1 className={`text-3xl font-bold mb-2 ${dk.textPrimary}`} data-id="element-9397" data-mp="1u0qsx5p9revh">
                {data.label} Contributions
              </h1>
              <p className={`text-lg ${dk.textSecondary}`} data-id="element-9398" data-mp="1k0zwgyh5epsr">
                {data.description}
              </p>
            </div>
            <div className="text-right" data-id="element-9399" data-mp="27qaonrkfnbva">
              <div className={`text-3xl font-bold ${colors.text}`} data-id="element-9400" data-mp="27rcbsueq3otd">
                ${data.totalAmount.toLocaleString()}
              </div>
              <div className={`text-sm ${dk.textMuted}`} data-id="element-9401" data-mp="1y10um6613j4j">
                from {data.totalDonors} donors
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-9402" data-mp="22xrez5ov90vr">
          {/* Left Column: Breakdown */}
          <div className="lg:col-span-2 space-y-8" data-id="element-9403" data-mp="2qnlll5ildt7k">
            {/* Top Genres/Initiatives */}
            <div className={`${dk.card} rounded-xl p-6 border shadow-sm`} data-id="element-9404" data-mp="2vhznzrvo90lq">
              <h2 className={`text-xl font-bold mb-6 ${dk.textPrimary}`} data-id="element-9405" data-mp="jzl7ze6414nq">
                Top {type === 'platform' ? 'Initiatives' : 'Genres'}
              </h2>
              <div className="space-y-4" data-id="element-9406" data-mp="tr8vx53gk3kg">
                {((data as any).topGenres || (data as any).topInitiatives).map((item: any) => <div key={item.genre || item.name} className="flex items-center gap-4" data-id="element-9407" data-mp="ou8reym9b8vg">
                      <div className="flex-1" data-id="element-9408" data-mp="3s9wlqct37q1t">
                        <div className="flex justify-between mb-1" data-id="element-9409" data-mp="a304ix9qo1m0">
                          <span className={`font-medium ${dk.textPrimary}`} data-id="element-9410" data-mp="a4b8oy73jbdn">
                            {item.genre || item.name}
                          </span>
                          <span className={`font-medium ${dk.textSecondary}`} data-id="element-9411" data-mp="1lhdmjlht9mu4">
                            {item.percentage}%
                          </span>
                        </div>
                        <div className={`overflow-hidden h-2.5 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-9412" data-mp="zr96xcf81kk6">
                          <div style={{
                      width: `${item.percentage}%`
                    }} className={`h-full rounded-full ${type === 'authors' ? 'bg-blue-500' : type === 'readers' ? 'bg-emerald-500' : type === 'vendors' ? 'bg-amber-500' : 'bg-rose-500'}`} data-id="element-9413" data-mp="qyfqhbo1gf4q" />
                        </div>
                      </div>
                    </div>)}
              </div>
            </div>

            {/* Recent Activity */}
            <div className={`${dk.card} rounded-xl p-6 border shadow-sm`} data-id="element-9414" data-mp="32balprtpekw">
              <h2 className={`text-xl font-bold mb-6 ${dk.textPrimary}`} data-id="element-9415" data-mp="22c9rqmi75i0e">
                Recent Activity
              </h2>
              <div className="overflow-x-auto" data-id="element-9416" data-mp="1xf9nmh4wqpbx">
                <table className="min-w-full" data-id="element-9417" data-mp="24yir5tqqftt2">
                  <thead data-id="element-9418" data-mp="24xqzgu09ag82">
                    <tr className={`border-b ${dk.divider}`} data-id="element-9419" data-mp="29x3tpztgipko">
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${dk.textMuted}`} data-id="element-9420" data-mp="29vix84nl0wk6">
                        Date
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${dk.textMuted}`} data-id="element-9421" data-mp="2joq2lpibock9">
                        Donor
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${dk.textMuted}`} data-id="element-9422" data-mp="1vwt2uws5dazi">
                        Cause
                      </th>
                      <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${dk.textMuted}`} data-id="element-9423" data-mp="37bpvegnjiibm">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${dk.divider}`} data-id="element-9424" data-mp="17b1cg8npngls">
                    {categoryDonations.map((donation, index) => <tr key={index} className={`text-sm ${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} transition-colors`} data-id="element-9425" data-mp="1l9hn2aop1r97" data-mpi={index}>
                        <td className={`px-4 py-3 ${dk.textMuted}`} data-id="element-9426" data-mp="sllt3dqiedg4">
                          {new Date(donation.date).toLocaleDateString()}
                        </td>
                        <td className={`px-4 py-3 font-medium ${dk.textPrimary}`} data-id="element-9427" data-mp="12bx6dqw31yzo">
                          {donation.donor}
                        </td>
                        <td className={`px-4 py-3 ${dk.textSecondary}`} data-id="element-9428" data-mp="9tjfkb95zda3">
                          {donation.cause}
                        </td>
                        <td className={`px-4 py-3 text-right font-semibold ${dk.textPrimary}`} data-id="element-9429" data-mp="2gj4o3tbvhmsb">
                          ${donation.amount.toLocaleString()}
                        </td>
                      </tr>)}
                    {categoryDonations.length === 0 && <tr data-id="element-9430" data-mp="2cikomj3tu730">
                        <td colSpan={4} className={`px-4 py-8 text-center ${dk.textMuted}`} data-id="element-9431" data-mp="2chrklpw95vok">
                          No recent donations found.
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right Column: Nonprofits */}
          <div className="space-y-6" data-id="element-9432" data-mp="1et7yu520lx07">
            <h2 className={`text-xl font-bold ${dk.textPrimary}`} data-id="element-9433" data-mp="1es6aiyvzne0j">
              Supported Causes
            </h2>
            {data.nonprofits.map((nonprofit, index) => <div key={index} className={`${dk.card} rounded-xl p-5 border shadow-sm hover:shadow-md transition-shadow`} data-id="element-9434" data-mp="1z2qx5lh6jwz4" data-mpi={index}>
                <div className="flex justify-between items-start mb-2" data-id="element-9435" data-mp="1qa6zoocmjyfv">
                  <span className={`inline-block px-2 py-1 ${rt.badgeRadius} text-xs font-medium ${darkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`} data-id="element-9436" data-mp="1q82vs6eq6zfl">
                    {nonprofit.theme}
                  </span>
                  <span className={`text-lg font-bold ${dk.textPrimary}`} data-id="element-9437" data-mp="2e09limpy4861">
                    ${nonprofit.amount.toLocaleString()}
                  </span>
                </div>
                <h3 className={`font-bold text-lg mb-1 ${dk.textPrimary}`} data-id="element-9438" data-mp="240iskph1a182">
                  {nonprofit.name}
                </h3>
                <p className={`text-sm mb-4 ${dk.textMuted}`} data-id="element-9439" data-mp="xmt52wwefeyx">
                  {nonprofit.donationCount} donations from {data.label}
                </p>
                <div className={`text-xs pt-3 border-t ${dk.border} ${dk.textSecondary}`} data-id="element-9440" data-mp="ij9not25e9rw">
                  Top contributor:{' '}
                  <span className="font-medium" data-id="element-9441" data-mp="sgo2ko8faqi3">
                    {nonprofit.mainContributors}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
};
export default CategoryDetail;